/**
 * Utility functions for safe Notification API access
 * Prevents ReferenceError on iOS Safari and other browsers without Notification support
 */

import NotificationWrapper from './notificationWrapper';

export const notificationUtils = {
  /**
   * Check if Notification API is available
   */
  isSupported(): boolean {
    return NotificationWrapper.isNotificationSupported();
  },

  /**
   * Get current permission status safely
   */
  getPermission(): NotificationPermission | 'not-supported' {
    return NotificationWrapper.getPermission() as NotificationPermission | 'not-supported';
  },

  /**
   * Check if permission is granted
   */
  isGranted(): boolean {
    return this.getPermission() === 'granted';
  },

  /**
   * Check if permission is denied
   */
  isDenied(): boolean {
    return this.getPermission() === 'denied';
  },

  /**
   * Check if permission is default (not asked)
   */
  isDefault(): boolean {
    return this.getPermission() === 'default';
  },

  /**
   * Request permission safely
   */
  async requestPermission(): Promise<NotificationPermission | 'not-supported'> {
    const permission = await NotificationWrapper.requestPermission();
    return permission as NotificationPermission | 'not-supported';
  },

  /**
   * Show notification safely (for testing)
   */
  async showNotification(title: string, options?: NotificationOptions): Promise<boolean> {
    if (!this.isSupported() || !this.isGranted()) {
      return false;
    }

    try {
      new (window as Window & { Notification: new (title: string, options?: NotificationOptions) => Notification }).Notification(title, options);
      return true;
    } catch (error) {
      console.error('Error showing notification:', error);
      return false;
    }
  }
};

// Export individual functions for convenience
export const isNotificationSupported = () => notificationUtils.isSupported();
export const getNotificationPermission = () => notificationUtils.getPermission();
export const isNotificationGranted = () => notificationUtils.isGranted();
export const isNotificationDenied = () => notificationUtils.isDenied();
export const isNotificationDefault = () => notificationUtils.isDefault();
export const requestNotificationPermission = () => notificationUtils.requestPermission();